import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { logError } from '@/lib/logger';
import { cn } from '@/lib/utils';

export type SubscriptionBannerPayload = {
  type: 'pending_payment' | 'past_due' | 'limit_reached';
  checkoutUrl?: string | null;
};

type SubscriptionBannerProps = {
  banner: SubscriptionBannerPayload;
  clientCount: number;
  clientLimit: number | null;
  className?: string;
};

const getSemantic = (type: SubscriptionBannerPayload['type']) => {
  switch (type) {
    case 'past_due':
      return 'destructive' as const;
    case 'pending_payment':
      return 'warning' as const;
    case 'limit_reached':
      return 'warning' as const;
    default:
      return 'warning' as const;
  }
};

export default function SubscriptionBanner({
  banner,
  clientCount,
  clientLimit,
  className,
}: SubscriptionBannerProps) {
  const [isHidden, setIsHidden] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const content = useMemo(() => {
    if (banner.type === 'pending_payment') {
      return {
        title: 'Pagamento in sospeso',
        description: 'Completa il pagamento per mantenere attivo il tuo abbonamento.',
      };
    }
    if (banner.type === 'past_due') {
      return {
        title: 'Pagamento in ritardo',
        description:
          "Verifica il pagamento o contatta l'amministrazione per evitare la sospensione.",
      };
    }
    return {
      title: 'Limite clienti raggiunto',
      description: clientLimit
        ? `Hai raggiunto il limite di ${clientLimit} clienti attivi o invitati.`
        : 'Hai raggiunto il limite clienti del tuo abbonamento.',
    };
  }, [banner.type, clientLimit]);

  const handleDismiss = async () => {
    try {
      setIsHidden(true);
      setFeedback(null);
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      await fetch('/therapists/subscription/dismiss_banner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
      });
    } catch (error) {
      logError('Errore dismiss banner abbonamento', error);
      setIsHidden(false);
    }
  };

  const handleResend = async () => {
    if (isSending) return;
    setIsSending(true);
    setFeedback(null);
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/resend_payment_link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
      });

      if (response.ok) {
        setFeedback('Link inviato via email.');
      } else {
        setFeedback('Impossibile inviare il link. Riprova più tardi.');
      }
    } catch (error) {
      logError('Errore reinvio link pagamento', error);
      setFeedback('Impossibile inviare il link. Riprova più tardi.');
    } finally {
      setIsSending(false);
    }
  };

  if (isHidden) return null;

  const semantic = getSemantic(banner.type);

  return (
    <Card variant="flat" semantic={semantic} className={cn('gap-3 px-4 py-3', className)}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <p>{content.title}</p>
          <p className="text-sm text-muted-foreground">{content.description}</p>
          {banner.type === 'limit_reached' && clientLimit ? (
            <p className="text-sm text-muted-foreground">
              Clienti attuali: {clientCount} / {clientLimit}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {banner.type === 'pending_payment' && banner.checkoutUrl ? (
            <Button asChild>
              <a
                href={banner.checkoutUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Completa il pagamento"
              >
                Completa pagamento
              </a>
            </Button>
          ) : null}
          {banner.type === 'pending_payment' && !banner.checkoutUrl ? (
            <Button onClick={handleResend} disabled={isSending} aria-label="Reinvia link pagamento">
              {isSending ? 'Invio...' : 'Reinvia link'}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={handleDismiss} aria-label="Nascondi avviso">
            Nascondi
          </Button>
        </div>
      </div>
      {feedback ? <p className="text-sm text-muted-foreground">{feedback}</p> : null}
    </Card>
  );
}
